<script setup lang="ts">
import { RestaurantLiteWithDistance } from '~/lib/interfaces/restaurant'
import { Coords } from '~/lib/models/geolocation'

const props = defineProps<{
  restaurant: RestaurantLiteWithDistance
  smallCard?: boolean
}>()

const { t } = useLocale()
const { restaurantDetailRoute } = useMcdRoutes()
const appStore = useAppStore()
const authStore = useAuthStore()

const emit = defineEmits(['select-restaurant'])

const closeModals = () => {
  const restaurantStore = useRestaurantStore()
  restaurantStore.closeModal()
  restaurantStore.closeChangeRestaurantModal()
}

const { isFavorite, toggleFavorite } = useFavoriteRestaurant(props.restaurant.code)

const restaurantPosition: Coords = {
  lat: props.restaurant?.coordinates.latitude || 0,
  lng: props.restaurant?.coordinates.longitude || 0,
}

const { distanceFromUser } = useUserLocation()
</script>

<template>
  <div
    data-cy="restaurant-item"
    class="m-4 flex flex-col gap-4 rounded-lg bg-white px-6 py-4 md:m-0"
    :class="smallCard ? '' : ' md:flex md:flex-row md:gap-4 md:px-0'"
  >
    <div v-if="authStore.isUserLogged && !smallCard" class="w-8">
      <img
        class="cursor-pointer"
        :src="
          isFavorite
            ? '/images/icons/favoriteFilled.svg'
            : '/images/icons/profileSections/favorite.svg'
        "
        alt="favorite icon"
        @click="toggleFavorite"
      />
    </div>
    <div class="w-full">
      <RestaurantName
        :name="props.restaurant.name"
        :show-if-favorite="smallCard ?? true"
        :restaurant-code="$props.restaurant.code"
        :city="$props.restaurant.city"
        :is-favorite="isFavorite"
        @toggle-favorite="toggleFavorite"
        @close="closeModals"
      />
      <RestaurantDirection
        :address="props.restaurant.address"
        :city="props.restaurant.city"
        :distance-from-user="distanceFromUser(restaurantPosition)"
      />
      <RestaurantState :restaurant="props.restaurant" />
    </div>
    <div
      class="flex w-full flex-row items-end justify-end gap-4 md:flex-row"
      :class="smallCard ? '' : 'md:flex md:items-end md:justify-end'"
    >
      <NuxtLink
        v-if="!smallCard"
        class="btn-secondary flex h-10 w-32 items-center justify-center py-1 text-sm leading-4"
        :class="smallCard ? '' : 'md:h-fit md:w-24 md:py-1 md:text-xs'"
        :to="restaurantDetailRoute(restaurant.code, restaurant.name, restaurant.city)"
        @click="closeModals"
      >
        {{ t('common.seeDetail') }}
      </NuxtLink>

      <McdButton
        v-if="!appStore.isEcommerceDisabled && restaurant.ecommerce"
        class="h-h-10 w-32 py-1 text-sm"
        :class="smallCard ? '' : 'md:h-fit md:w-24 md:py-1 md:text-xs'"
        data-test="btn-select-restaurant"
        is-primary
        @click="emit('select-restaurant', restaurant)"
      >
        {{ t('common.start') }}
      </McdButton>
    </div>
  </div>
</template>
